import React from 'react';
import { lineDiff } from '../../utils/lineDiff';
import CopyButton from './CopyButton';
import '../PaymentPorterPage.css';
import './DiffModal.css';

// Side-by-side line diff of two documents, in a modal.
//
// Built shared rather than inside the Rotana page because the same "what changed between these two
// payloads" question is asked all over this product — Payment Porter's dry run and run detail already
// render the identical `pp-diff-*` markup inline, three separate times. This wraps the existing
// utils/lineDiff and the existing .pp-modal-* / .pp-diff-* classes; it introduces no new diff algorithm
// and no new visual language, so anywhere that adopts it looks like everywhere else.
//
// `left` and `right` are STRINGS, already formatted by the caller. That is deliberate: the caller knows
// whether it holds XML, JSON or plain text (see formatMessage), and a diff of prettified text is the only
// one that reads sensibly — diffing two single-line documents produces one enormous +/- pair that says
// nothing about what actually differs.
const DiffModal = ({
    title = 'Diff',
    subtitle,
    leftLabel = 'Before',
    rightLabel = 'After',
    left,
    right,
    onClose,
}) => {
    const a = left || '';
    const b = right || '';
    const lines = lineDiff(a, b);
    const changed = lines.filter(l => l.t !== 'same').length;

    return (
        <div className="pp-modal-backdrop" onClick={onClose}>
            <div className="pp-modal diff-modal" onClick={e => e.stopPropagation()}>
                <div className="pp-modal-header">
                    <div className="diff-modal-title">
                        <span>{title}</span>
                        {subtitle && <span className="diff-modal-subtitle">{subtitle}</span>}
                    </div>
                    <div className="diff-modal-head-actions">
                        <span className={`diff-modal-count${changed === 0 ? ' diff-modal-count-none' : ''}`}>
                            {changed === 0 ? 'Identical' : `${changed} changed line${changed === 1 ? '' : 's'}`}
                        </span>
                        <button className="pp-modal-close" onClick={onClose} title="Close">×</button>
                    </div>
                </div>

                <div className="pp-modal-body">
                    <div className="diff-modal-legend">
                        <span className="diff-modal-legend-item"><span className="pp-diff-del">-</span> {leftLabel}</span>
                        <span className="diff-modal-legend-item"><span className="pp-diff-add">+</span> {rightLabel}</span>
                        <CopyButton
                            value={lines.map(l => `${l.t === 'add' ? '+' : l.t === 'del' ? '-' : ' '}${l.s}`).join('\n')}
                            label="Copy diff"
                            className="diff-modal-copy"
                        />
                    </div>

                    {changed === 0 ? (
                        <div className="pp-modal-empty">
                            {leftLabel} and {rightLabel} are identical.
                        </div>
                    ) : (
                        <pre className="diff-modal-pre">
                            {lines.map((l, i) => (
                                <div key={i} className={l.t === 'add' ? 'pp-diff-add' : l.t === 'del' ? 'pp-diff-del' : 'pp-diff-same'}>
                                    <span className="pp-diff-gutter">{l.t === 'add' ? '+' : l.t === 'del' ? '-' : ' '}</span>{l.s}
                                </div>
                            ))}
                        </pre>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DiffModal;
